import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { PlotSpec } from '../threshold-function-preview';
import type { ThresholdSourceBindingItem } from './threshold-bundle-form.hook';
import type {
  ThresholdFormulaPreviewDescriptor,
  ThresholdFormulaPreviewInput,
  ThresholdFormulaPreviewProvider,
} from './threshold-formula-preview-provider';

// -- type definitions --

export interface ThresholdFormulaPreviewPanelProps {
  values: ThresholdFormulaPreviewInput['values'];
  sourceBindings: ThresholdSourceBindingItem[];
  provider: ThresholdFormulaPreviewProvider;
  height?: number;
  boundKind?: 'minimum' | 'maximum';
  fillColor?: string;
}

// -- constants --

const DEFAULT_HEIGHT = 220;

// -- functions --

const decoratePlotSpecWithStyle = (
  plotSpec: PlotSpec,
  dash: string,
  width: number
): PlotSpec => {
  const safeWidth = Number.isFinite(width) && width > 0 ? width : 2;

  return {
    ...plotSpec,
    data: plotSpec.data.map((series) => ({
      ...series,
      attr: {
        ...(series.attr ?? {}),
        'stroke-width': safeWidth,
        ...(dash ? { 'stroke-dasharray': dash } : {}),
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
      },
    })),
  };
};

// -- main component --

export function ThresholdFormulaPreviewPanel({
  values,
  sourceBindings,
  provider,
  height = DEFAULT_HEIGHT,
  boundKind,
  fillColor,
}: ThresholdFormulaPreviewPanelProps) {
  const plotRef = useRef<HTMLDivElement | null>(null);
  const [descriptor, setDescriptor] =
    useState<ThresholdFormulaPreviewDescriptor | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasRenderedPlot, setHasRenderedPlot] = useState(false);

  const formulaMarkup = useMemo(() => {
    const latex = descriptor?.latexFormula ?? 'f(x)=0';
    return katex.renderToString(latex, {
      throwOnError: false,
      displayMode: true,
      strict: 'ignore',
    });
  }, [descriptor?.latexFormula]);

  useEffect(() => {
    let isDisposed = false;

    const loadDescriptor = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const nextDescriptor = await provider({
          values,
          sourceBindings,
        });

        if (!isDisposed) {
          setDescriptor(nextDescriptor);
        }
      } catch {
        if (!isDisposed) {
          setDescriptor(null);
          setError("Impossible de calculer l'aperçu de la formule.");
        }
      } finally {
        if (!isDisposed) {
          setIsLoading(false);
        }
      }
    };

    void loadDescriptor();

    return () => {
      isDisposed = true;
    };
  }, [provider, sourceBindings, values]);

  useEffect(() => {
    const plotElement = plotRef.current;

    if (!plotElement || typeof window === 'undefined' || !descriptor) {
      return;
    }

    if (process.env.NODE_ENV === 'test') {
      return;
    }

    let isDisposed = false;
    let resizeObserver: ResizeObserver | undefined;

    const renderPlot = async () => {
      try {
        const functionPlotModule = await import('function-plot');
        const functionPlot = functionPlotModule.default ?? functionPlotModule;

        if (isDisposed) {
          return;
        }

        const width = Math.max(plotElement.clientWidth || 560, 320);
        const styledSpec = decoratePlotSpecWithStyle(
          descriptor.plotSpec,
          values.default_style.dash,
          Number(values.default_style.width)
        );

        plotElement.innerHTML = '';

        functionPlot({
          target: plotElement,
          width,
          height,
          disableZoom: true,
          grid: true,
          xAxis: { domain: styledSpec.xDomain },
          yAxis: { domain: styledSpec.yDomain },
          data: [...styledSpec.data],
        });

        setHasRenderedPlot(true);
      } catch {
        if (!isDisposed) {
          plotElement.innerHTML = '';
          setHasRenderedPlot(false);
        }
      }
    };

    void renderPlot();

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        void renderPlot();
      });
      resizeObserver.observe(plotElement);
    }

    return () => {
      isDisposed = true;
      resizeObserver?.disconnect();
      plotElement.innerHTML = '';
    };
  }, [
    descriptor,
    height,
    values.default_style.dash,
    values.default_style.width,
    boundKind,
    fillColor,
  ]);

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" fontWeight={700}>
        Formule
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
        <Box
          sx={{
            '& .katex-display': { margin: 0 },
            overflowX: 'auto',
          }}
          dangerouslySetInnerHTML={{ __html: formulaMarkup }}
        />
      </Paper>

      <Typography variant="subtitle2" fontWeight={700}>
        Représentation graphique
      </Typography>
      <Paper
        variant="outlined"
        sx={{ p: 1.5, bgcolor: '#fff', minHeight: `${height}px` }}
      >
        {isLoading ? (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{ minHeight: `${height}px` }}
          >
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Génération de la prévisualisation...
            </Typography>
          </Stack>
        ) : error ? (
          <Alert severity="warning">{error}</Alert>
        ) : (
          <Box>
            <Box
              ref={plotRef}
              sx={{ width: '100%', minHeight: `${height}px` }}
            />
            {!hasRenderedPlot && (
              <Typography variant="caption" color="text.secondary">
                Aperçu indisponible pour ce type de seuil.
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    </Stack>
  );
}
